/**
 * CommunicationPage — Module Communication (v4.5.0)
 * 2 onglets : Gazettes | Réseaux sociaux
 * Agent Sirius — co-écriture IA
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Newspaper, Plus, Search, Edit, Trash2, Eye, Send, CheckCircle, Clock,
  AlertCircle, ArrowLeft, MessageSquare, FileText, ChevronRight, Loader,
  RotateCcw, Share2, Image, Sparkles, Download, ExternalLink, Pencil
} from 'lucide-react';
import GazettePhotoManager from '../components/communication/GazettePhotoManager';
import TipTapEditor from '../components/communication/TipTapEditor';
import {
  getGazettes, getGazettesMultiStructures, getGazette, createGazette, updateGazette, deleteGazette,
  createGazetteSection, updateGazetteSection, deleteGazetteSection, reorderGazetteSections,
  createGazetteItem, updateGazetteItem, deleteGazetteItem,
  submitGazette, validateGazette, returnGazette, publishGazette,
  getGazetteComments, createGazetteComment, deleteGazetteComment,
  getGazetteSectionTypes,
  generateGazetteFullText, generateGazetteSectionText,
  getGazettePdfUrl, getGazettePreviewUrl
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import Modal from '../components/ui/Modal';

// ─── Constantes ───────────────────────────────────────────────────────────────

const GAZETTE_STATUS = {
  draft:     { label: 'Brouillon',   color: 'bg-gray-100 text-gray-600',   icon: Clock },
  review:    { label: 'En révision', color: 'bg-amber-100 text-amber-700', icon: Eye },
  validated: { label: 'Validée',     color: 'bg-blue-100 text-blue-700',   icon: CheckCircle },
  published: { label: 'Publiée',     color: 'bg-green-100 text-green-700', icon: FileText },
};

const MONTH_NAMES = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = GAZETTE_STATUS[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      {Icon && <Icon size={12} />}
      {cfg.label}
    </span>
  );
};

// ─── GazetteList ──────────────────────────────────────────────────────────────

function GazetteList({ gazettes, loading, onSelect, onCreate }) {
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);

  const filtered = gazettes.filter(g => !yearFilter || g.year === yearFilter);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <select
            value={yearFilter || ''}
            onChange={e => setYearFilter(e.target.value ? parseInt(e.target.value) : null)}
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value={currentYear}>{currentYear}</option>
            <option value={currentYear - 1}>{currentYear - 1}</option>
            <option value="">Toutes les années</option>
          </select>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          Nouvelle gazette
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader className="animate-spin mr-2" size={20} />
          Chargement...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Newspaper size={48} className="mx-auto mb-3 opacity-50" />
          <p>Aucune gazette pour cette période</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(g => (
            <div
              key={g.id}
              onClick={() => onSelect(g)}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                    {MONTH_NAMES[g.month]?.substring(0, 3)}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{g.title}</h3>
                    <p className="text-xs text-gray-500">
                      {g.structure?.name} — {MONTH_NAMES[g.month]} {g.year}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs text-gray-400">
                    <div>{g._count?.sections || 0} sections</div>
                    <div>{g._count?.photos || 0} photos</div>
                  </div>
                  <StatusBadge status={g.status} />
                  <ChevronRight size={16} className="text-gray-300" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GazetteEditor ────────────────────────────────────────────────────────────

function GazetteEditor({ gazetteId, onBack, user }) {
  const toast = useToast();
  const [gazette, setGazette] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('edit');
  const [newComment, setNewComment] = useState('');
  const [addItemSection, setAddItemSection] = useState(null);
  const [editItemId, setEditItemId] = useState(null);
  const [itemForm, setItemForm] = useState({ title: '', description: '', date: '' });
  const debounceTimers = useRef({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingSection, setGeneratingSection] = useState(null);

  const loadGazette = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getGazette(gazetteId);
      setGazette(data);
    } catch (err) {
      toast.error('Erreur chargement gazette');
    } finally {
      setLoading(false);
    }
  }, [gazetteId, toast]);

  useEffect(() => { loadGazette(); }, [loadGazette]);

  const handleWorkflow = async (action) => {
    try {
      if (action === 'submit') await submitGazette(gazetteId);
      else if (action === 'validate') await validateGazette(gazetteId);
      else if (action === 'publish') await publishGazette(gazetteId);
      toast.success(`Gazette ${action === 'submit' ? 'soumise' : action === 'validate' ? 'validée' : 'publiée'}`);
      loadGazette();
    } catch (err) {
      toast.error('Erreur changement de statut');
    }
  };

  const handleReturn = async () => {
    const comment = prompt('Commentaire pour le renvoi en brouillon (optionnel) :');
    try {
      await returnGazette(gazetteId, comment || '');
      toast.success('Gazette renvoyée en brouillon');
      loadGazette();
    } catch (err) {
      toast.error('Erreur renvoi');
    }
  };

  const handleUpdateSection = async (sectionId, data) => {
    try {
      await updateGazetteSection(gazetteId, sectionId, data);
      // Don't reload gazette for content updates (avoids cursor reset in TipTap)
      if (!data.content && !data.contentHtml) loadGazette();
    } catch (err) {
      toast.error('Erreur mise à jour section');
    }
  };

  // Debounced save for TipTap content (1.5s delay)
  const handleSectionContentChange = (sectionId, html) => {
    if (debounceTimers.current[sectionId]) clearTimeout(debounceTimers.current[sectionId]);
    debounceTimers.current[sectionId] = setTimeout(() => {
      handleUpdateSection(sectionId, { content: html, contentHtml: html });
    }, 1500);
  };

  const handleGenerateAll = async () => {
    if (!window.confirm('Sirius va générer le texte de toutes les sections. Le contenu existant sera remplacé. Continuer ?')) return;
    try {
      setGeneratingAll(true);
      await generateGazetteFullText(gazetteId);
      toast.success('Texte généré par Sirius pour toutes les sections');
      loadGazette();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur génération IA');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleGenerateSection = async (sectionId) => {
    try {
      setGeneratingSection(sectionId);
      await generateGazetteSectionText(gazetteId, sectionId);
      toast.success('Section générée par Sirius');
      loadGazette();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur génération IA');
    } finally {
      setGeneratingSection(null);
    }
  };

  const handleAddItem = async (sectionId) => {
    if (!itemForm.title) return toast.error('Le titre est requis');
    try {
      await createGazetteItem(gazetteId, sectionId, itemForm);
      setItemForm({ title: '', description: '', date: '' });
      setAddItemSection(null);
      toast.success('Item ajouté');
      loadGazette();
    } catch (err) {
      toast.error('Erreur ajout item');
    }
  };

  const startEditItem = (item) => {
    setEditItemId(item.id);
    setItemForm({ title: item.title || '', description: item.description || '', date: item.date ? item.date.substring(0, 10) : '' });
  };

  const handleEditItem = async (itemId) => {
    try {
      await updateGazetteItem(gazetteId, itemId, itemForm);
      setEditItemId(null);
      setItemForm({ title: '', description: '', date: '' });
      toast.success('Item modifié');
      loadGazette();
    } catch (err) {
      toast.error('Erreur modification item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Supprimer cet item ?')) return;
    try {
      await deleteGazetteItem(gazetteId, itemId);
      toast.success('Item supprimé');
      loadGazette();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await createGazetteComment(gazetteId, { content: newComment });
      setNewComment('');
      toast.success('Commentaire ajouté');
      loadGazette();
    } catch (err) {
      toast.error('Erreur ajout commentaire');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader className="animate-spin mr-2" size={20} />
        Chargement gazette...
      </div>
    );
  }

  if (!gazette) return <div className="text-center py-12 text-gray-400">Gazette non trouvée</div>;

  const isEditable = gazette.status === 'draft' || gazette.status === 'review';
  const isAdmin = user?.role === 'admin';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="font-semibold text-lg text-gray-900">{gazette.title}</h2>
            <p className="text-sm text-gray-500">{gazette.structure?.name} — {MONTH_NAMES[gazette.month]} {gazette.year}</p>
          </div>
          <StatusBadge status={gazette.status} />
        </div>
        <div className="flex items-center gap-2">
          {isEditable && (
            <button
              onClick={handleGenerateAll}
              disabled={generatingAll}
              className="flex items-center gap-1.5 bg-violet-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-violet-700 disabled:opacity-50"
            >
              {generatingAll ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {generatingAll ? 'Sirius rédige...' : 'Générer tout (IA)'}
            </button>
          )}
          {gazette.status === 'draft' && (
            <button onClick={() => handleWorkflow('submit')} className="flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-amber-600">
              <Send size={14} /> Soumettre
            </button>
          )}
          {gazette.status === 'review' && isAdmin && (
            <>
              <button onClick={handleReturn} className="flex items-center gap-1.5 border border-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50">
                <RotateCcw size={14} /> Renvoyer
              </button>
              <button onClick={() => handleWorkflow('validate')} className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700">
                <CheckCircle size={14} /> Valider
              </button>
            </>
          )}
          {gazette.status === 'validated' && isAdmin && (
            <button onClick={() => handleWorkflow('publish')} className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700">
              <FileText size={14} /> Publier
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-gray-200 mb-4">
        {[
          { id: 'edit', label: 'Éditer', icon: Edit },
          { id: 'photos', label: `Photos (${gazette.photos?.length || 0})`, icon: Image },
          { id: 'preview', label: 'Aperçu', icon: Eye },
          { id: 'comments', label: `Commentaires (${gazette.comments?.length || 0})`, icon: MessageSquare },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu onglet */}
      {activeTab === 'edit' && (
        <div className="space-y-4">
          {gazette.sections?.map(section => (
            <div key={section.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <span>{section.sectionType?.emoji || '📄'}</span>
                  <span>{section.title || section.sectionType?.name || 'Section'}</span>
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{section.items?.length || 0} items</span>
                  {isEditable && (
                    <>
                      <button
                        onClick={() => handleGenerateSection(section.id)}
                        disabled={generatingSection === section.id}
                        className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
                        title="Générer le texte avec Sirius (IA)"
                      >
                        {generatingSection === section.id ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        IA
                      </button>
                      <button
                        onClick={() => setAddItemSection(addItemSection === section.id ? null : section.id)}
                        className="text-xs text-primary hover:text-primary-dark font-medium"
                      >
                        + Item
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Éditeur rich text TipTap */}
              <TipTapEditor
                content={section.content || ''}
                onChange={html => handleSectionContentChange(section.id, html)}
                placeholder={`Contenu de la section "${section.title || section.sectionType?.name}"...`}
                disabled={!isEditable}
              />

              {/* Items de la section */}
              {section.items?.length > 0 && (
                <div className="mt-3 space-y-2">
                  {section.items.map(item => (
                    editItemId === item.id ? (
                      <div key={item.id} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-2">
                        <input
                          type="text"
                          value={itemForm.title}
                          onChange={e => setItemForm(f => ({ ...f, title: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-1.5 text-sm"
                          placeholder="Titre"
                        />
                        <textarea
                          value={itemForm.description}
                          onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none"
                          rows={2}
                          placeholder="Description"
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={itemForm.date}
                            onChange={e => setItemForm(f => ({ ...f, date: e.target.value }))}
                            className="border rounded-lg px-3 py-1.5 text-sm"
                          />
                          <button onClick={() => handleEditItem(item.id)} className="bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-dark">
                            Sauvegarder
                          </button>
                          <button onClick={() => { setEditItemId(null); setItemForm({ title: '', description: '', date: '' }); }} className="text-gray-400 hover:text-gray-600 text-sm">
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={item.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-primary">🟠</span>
                          <span className="text-sm font-medium text-gray-800">{item.title || 'Sans titre'}</span>
                          {item.date && <span className="text-xs text-gray-400">{fmtDate(item.date)}</span>}
                          {item.photos?.length > 0 && (
                            <span className="text-xs text-gray-400">📷 {item.photos.length}</span>
                          )}
                        </div>
                        {isEditable && (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => startEditItem(item)} className="text-gray-400 hover:text-primary">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => handleDeleteItem(item.id)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              )}

              {/* Formulaire ajout item */}
              {addItemSection === section.id && (
                <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
                  <input
                    type="text"
                    placeholder="Titre de l'item"
                    value={itemForm.title}
                    onChange={e => setItemForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm"
                  />
                  <textarea
                    placeholder="Description (optionnel)"
                    value={itemForm.description}
                    onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm resize-none"
                    rows={2}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={itemForm.date}
                      onChange={e => setItemForm(f => ({ ...f, date: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm"
                    />
                    <button
                      onClick={() => handleAddItem(section.id)}
                      className="bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-dark"
                    >
                      Ajouter
                    </button>
                    <button
                      onClick={() => { setAddItemSection(null); setItemForm({ title: '', description: '', date: '' }); }}
                      className="text-gray-400 hover:text-gray-600 text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'photos' && (
        <GazettePhotoManager
          gazetteId={gazetteId}
          photos={gazette.photos || []}
          sectionItems={gazette.sections || []}
          onRefresh={loadGazette}
        />
      )}

      {activeTab === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <a
              href={getGazettePreviewUrl(gazetteId)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 border border-gray-300 px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              <ExternalLink size={14} /> Ouvrir l'aperçu
            </a>
            <a
              href={getGazettePdfUrl(gazetteId)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 bg-primary text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-dark"
            >
              <Download size={14} /> Télécharger PDF
            </a>
          </div>
          <iframe
            src={getGazettePreviewUrl(gazetteId)}
            title="Aperçu gazette"
            className="w-full border rounded-xl"
            style={{ height: '800px' }}
          />
        </div>
      )}

      {activeTab === 'comments' && (
        <div className="space-y-4">
          {/* Formulaire nouveau commentaire */}
          <div className="flex gap-2">
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Ajouter un commentaire..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none"
              rows={2}
            />
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              className="self-end bg-primary text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-dark disabled:opacity-50"
            >
              Envoyer
            </button>
          </div>

          {/* Liste commentaires */}
          {gazette.comments?.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Aucun commentaire</p>
          ) : (
            gazette.comments?.map(c => (
              <div key={c.id} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">{c.authorName}</span>
                  <span className="text-xs text-gray-400">{fmtDate(c.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-600">{c.content}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── CommunicationPage (composant principal) ──────────────────────────────────

export default function CommunicationPage({ showAllStructures, user }) {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('gazettes');
  const [gazettes, setGazettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGazette, setSelectedGazette] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });

  const loadGazettes = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      if (showAllStructures) {
        const ids = structures.map(s => s.structureId || s.id).join(',');
        res = await getGazettesMultiStructures(ids);
      } else if (currentStructure?.id) {
        res = await getGazettes({ structureId: currentStructure?.id });
      } else {
        setGazettes([]);
        setLoading(false);
        return;
      }
      setGazettes(res.data || []);
    } catch (err) {
      toast.error('Erreur chargement gazettes');
    } finally {
      setLoading(false);
    }
  }, [currentStructure?.id, showAllStructures, structures, toast]);

  useEffect(() => { loadGazettes(); }, [loadGazettes]);

  const handleCreate = async () => {
    const structureId = showAllStructures ? createForm.structureId : currentStructure?.id;
    if (!structureId) return toast.error('Sélectionnez une structure');

    try {
      const { data } = await createGazette({
        structureId,
        month: parseInt(createForm.month),
        year: parseInt(createForm.year)
      });
      toast.success('Gazette créée');
      setShowCreateModal(false);
      setSelectedGazette(data.id);
      loadGazettes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur création gazette');
    }
  };

  const TABS = [
    { id: 'gazettes', label: 'Gazettes', icon: Newspaper },
    { id: 'social', label: 'Réseaux sociaux', icon: Share2 },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Newspaper size={24} className="text-primary" />
          Communication
        </h1>
      </div>

      {/* Onglets principaux */}
      {!selectedGazette && (
        <div className="flex border-b border-gray-200">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Contenu */}
      {selectedGazette ? (
        <GazetteEditor
          gazetteId={selectedGazette}
          onBack={() => { setSelectedGazette(null); loadGazettes(); }}
          user={user}
        />
      ) : activeTab === 'gazettes' ? (
        <GazetteList
          gazettes={gazettes}
          loading={loading}
          onSelect={g => setSelectedGazette(g.id)}
          onCreate={() => setShowCreateModal(true)}
        />
      ) : (
        <div className="text-center py-12 text-gray-400">
          <Share2 size={48} className="mx-auto mb-3 opacity-50" />
          <p>Module réseaux sociaux — Sprint 6</p>
        </div>
      )}

      {/* Modal création gazette */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Nouvelle gazette"
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Annuler
              </button>
              <button onClick={handleCreate} className="bg-primary text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-dark">
                Créer
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {showAllStructures && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Structure</label>
                <select
                  value={createForm.structureId || ''}
                  onChange={e => setCreateForm(f => ({ ...f, structureId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Sélectionner...</option>
                  {structures.map(s => (
                    <option key={s.structureId || s.id} value={s.structureId || s.id}>
                      {s.name || s.structure?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mois</label>
                <select
                  value={createForm.month}
                  onChange={e => setCreateForm(f => ({ ...f, month: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  {MONTH_NAMES.slice(1).map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                <select
                  value={createForm.year}
                  onChange={e => setCreateForm(f => ({ ...f, year: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  {[2025, 2026, 2027].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
